import React, { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Cancel } from "@mui/icons-material"; // Import icons from Material-UI
import { green, red } from "@mui/material/colors"; // Import colors for styling
import { useTheme } from "@mui/material/styles";
import { getToken, base_url } from "../../config/config";
import { tokens } from "../../theme";

const EmployeeSkillsMatrix = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = tokens(mode);

  const [employees, setEmployees] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillsMatrix, setSkillsMatrix] = useState([]);
  const [newSkill, setNewSkill] = useState(""); // State for the new skill input

  // Fetch employees and their skills from the API
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllEmployees`);
        const employeeData = response.data;
        setEmployees(employeeData);

        // Initialize the skills matrix based on employee skills
        const initialMatrix = employeeData.map((employee) =>
          skills.map((skill) => (employee.skills.includes(skill) ? 1 : 0)),
        );
        setSkillsMatrix(initialMatrix);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    fetchEmployees();
  }, [skills]); // Re-run when skills are updated

  // Fetch skills from the API
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllSkills`);
        const skillNames = response.data.map((skill) => skill.name);
        setSkills(skillNames);
      } catch (error) {
        console.error("Error fetching skills:", error);
      }
    };

    fetchSkills();
  }, []);

  // Toggle skill value (0 or 1) when a cell is clicked
  const toggleSkill = async (rowIndex, colIndex) => {
    const updatedMatrix = skillsMatrix.map((row, rIndex) =>
      row.map((cell, cIndex) => {
        if (rIndex === rowIndex && cIndex === colIndex) {
          return cell === 1 ? 0 : 1; // Toggle between 0 and 1
        }
        return cell;
      }),
    );

    setSkillsMatrix(updatedMatrix);

    // Update the employee's skills in the database
    try {
      const employee = employees[rowIndex];
      const skill = skills[colIndex];
      let updatedSkills;

      if (updatedMatrix[rowIndex][colIndex] === 1) {
        // Add skill to employee's skills array
        updatedSkills = [...employee.skills, skill];
      } else {
        // Remove skill from employee's skills array
        updatedSkills = employee.skills.filter(
          (empSkill) => empSkill !== skill,
        );
      }

      // Update the employee model in the backend
      await axios.put(`${base_url}/v1/updateEmployee/${employee._id}`, {
        skills: updatedSkills,
      });

      // Update local employee data to reflect the change
      const updatedEmployees = [...employees];
      updatedEmployees[rowIndex].skills = updatedSkills;
      setEmployees(updatedEmployees);
    } catch (error) {
      console.error("Error updating employee skills:", error);
    }
  };

  // Handle adding a new skill
  const addNewSkill = async () => {
    if (newSkill.trim() === "") return; // Prevent adding empty skill

    try {
      await axios.post(`${base_url}/v1/createSkill`, { name: newSkill });
      setSkills([...skills, newSkill]); // Add the new skill to the local state
      setNewSkill(""); // Clear the input field
    } catch (error) {
      console.error("Error adding new skill:", error);
    }
  };

  // Handle Enter key press for adding new skill
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      addNewSkill();
    }
  };

  const containerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    margin: 0,
    padding: "20px",
    overflow: "hidden",
  };

  const borderColor = mode === "dark" ? colors.grey[700] : colors.grey[300];
  const tableBackground = mode === "dark" ? colors.primary[400] : "#ffffff";
  const headerBackground =
    mode === "dark" ? colors.primary[500] : colors.grey[200];
  const textColor = mode === "dark" ? colors.grey[100] : colors.grey[900];

  const tableWrapperStyle = {
    flex: 1,
    overflowX: "auto",
    overflowY: "auto",
    border: `1px solid ${borderColor}`,
    borderRadius: "4px",
  };

  const tableStyle = {
    borderCollapse: "collapse",
    minWidth: "600px",
    width: "100%",
    backgroundColor: tableBackground,
  };

  const thTdStyle = {
    border: `1px solid ${borderColor}`,
    padding: "10px",
    textAlign: "center",
    cursor: "pointer",
    wordWrap: "break-word",
    whiteSpace: "nowrap",
    color: textColor,
  };

  const headerStyle = {
    ...thTdStyle,
    position: "sticky",
    top: 0,
    backgroundColor: headerBackground,
    zIndex: 100,
    fontWeight: "bold",
  };

  const employeeNameStyle = {
    fontWeight: "bold",
    position: "sticky",
    left: 0,
    backgroundColor: headerBackground,
    zIndex: 99,
    color: textColor,
  };

  const headerEmployeeNameStyle = {
    ...headerStyle,
    ...employeeNameStyle,
    zIndex: 101,
  };

  return (
    <div style={containerStyle}>
      <div style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerEmployeeNameStyle}></th>
              {skills.map((skill, index) => (
                <th key={index} style={headerStyle}>
                  {skill}
                </th>
              ))}
              <th style={headerStyle}>
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add new skill"
                  style={{
                    padding: "5px",
                    backgroundColor: tableBackground,
                    color: textColor,
                    border: `1px solid ${borderColor}`,
                    borderRadius: "3px",
                  }}
                />
                <button
                  onClick={addNewSkill}
                  style={{
                    marginLeft: "5px",
                    padding: "5px 10px",
                    backgroundColor: colors.greenAccent[500],
                    color: mode === "dark" ? "#000" : "#fff",
                    border: "none",
                    borderRadius: "3px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Add
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee, rowIndex) => (
              <tr key={employee._id}>
                <td style={{ ...thTdStyle, ...employeeNameStyle }}>
                  {employee.name}
                </td>
                {skillsMatrix[rowIndex] &&
                  skillsMatrix[rowIndex].map((skillValue, colIndex) => (
                    <td
                      key={colIndex}
                      style={{ ...thTdStyle }}
                      onClick={() => toggleSkill(rowIndex, colIndex)}
                    >
                      {skillValue === 1 ? (
                        <CheckCircle
                          style={{ color: colors.greenAccent[500] }}
                        /> // Green check icon
                      ) : (
                        <Cancel style={{ color: colors.redAccent[500] }} /> // Red cross icon
                      )}
                    </td>
                  ))}
                <td style={thTdStyle}></td>{" "}
                {/* Placeholder cell for "Add New Skill" column */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeSkillsMatrix;
